import { useCallback } from "react"

import { AB_TESTS, AB_TESTS_DATA } from "@trueskin-web/ab-experiments"
import { userClient } from "@trueskin-web/apis"
import { useMutation, useQuery, useQueryClient } from "@trueskin-web/apis"
import { useAuth } from "@trueskin-web/context"
import { storageService, vwoService } from "@trueskin-web/services"

const fetchUserMetadata = async (key) => {
  const defaultKeys = ["experiment_tags"]
  const response = await userClient.getUserMetadata({
    keys: [key, ...defaultKeys],
  })
  return response
}

const updateUserMetadata = async (data) => {
  await userClient.setUserMetadata(data)
}

const useCustomerMetadata = (key) => {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: userMetadata } = useQuery({
    queryKey: ["userMetadata", key],
    queryFn: () => fetchUserMetadata(key),
    enabled: !!key && !!user,
    staleTime: 60000,
  })

  const { mutateAsync: saveUserMetadata } = useMutation(updateUserMetadata, {
    onSuccess: () => {
      queryClient.invalidateQueries(["userMetadata", key])
    },
  })

  const saveAntiAgingMetadata = useCallback(async () => {
    const lsKey = AB_TESTS_DATA.antiAging?.lsKey

    if (!lsKey) {
      console.warn("Anti-aging experiment key is missing.")
      return
    }

    const testVariant = storageService.getItem({ id: lsKey })
    const testPresetType = storageService.getItem({
      id: storageService.getStorageKeys()?.VWO_TEST_PRESET_TYPE,
    })
    const questionnaireId = vwoService.getInitialQuestionnaireId()

    const experimentTags = userMetadata.experiment_tags || []
    const isAntiAgingExpTag = experimentTags.includes(AB_TESTS.AntiAging)
    const newExperimentTags = isAntiAgingExpTag
      ? experimentTags
      : [...experimentTags, AB_TESTS.AntiAging]

    const data = {
      antiaging_experiment: {
        test: lsKey,
        testVariant,
        testPresetType,
        questionnaireId,
      },
      experiment_tags: newExperimentTags,
    }

    await saveUserMetadata(data)
  }, [userMetadata])

  return {
    userMetadata,
    saveUserMetadata,
    saveAntiAgingMetadata,
  }
}

export default useCustomerMetadata
